import { computed, Ref } from 'vue';
import { CompanyPulseData } from '@/types/DataTypes';

/**
 * Comprehensive pulse statistics including participation and allocation metrics
 */
export interface PulseStats {
  /** Number of submitted pulse entries */
  submitted: number;
  /** Number of missing pulse entries */
  missing: number;
  /** Number of out-of-office entries */
  ooo: number;
  /** Total number of entries (submitted + missing + ooo) */
  total: number;
  /** Total number of eligible entries (excludes holidays and OOO) */
  totalEligibleEntries: number;
  /** Participation rate as percentage (submitted / (submitted + missing)) */
  participationRate: number;
  /** Company-wide time allocation aggregated by category */
  companyAllocation: Record<string, number>;
}

const OMIT_TYPES = ['HOLIDAY', 'OOO', 'FUTURE', 'TODAY'];

/**
 * Core function to calculate pulse statistics from company pulse data
 * @param pulses - Array of CompanyPulseData
 * @returns Complete pulse statistics
 */
function calculatePulseStats(pulses: CompanyPulseData[]): PulseStats {
  const stats: PulseStats = {
    submitted: 0,
    missing: 0,
    ooo: 0,
    total: 0,
    totalEligibleEntries: 0,
    participationRate: 0,
    companyAllocation: {}
  };

  if (!pulses || pulses.length === 0) {
    return stats;
  }

  let totalEntriesWithAllocation = 0;

  for (const pulse of pulses) {
    if (!pulse.pulses || Object.keys(pulse.pulses).length === 0) {
      continue;
    }
    
    for (const day of Object.keys(pulse.pulses)) {
      const dayData = pulse.pulses[day];
      const type = dayData?.type;
      
      // Handle special cases first
      if (OMIT_TYPES.includes(type)) {
        if (type === 'OOO') stats.ooo++;
        continue;
      }
      
      // This is an eligible entry (not holiday/OOO)
      stats.totalEligibleEntries++;
      
      // Check if this entry has time allocation data (backend now returns unallocated: 100 for missing data)
      if (dayData.timeAllocation && Object.keys(dayData.timeAllocation).length > 0) {
        totalEntriesWithAllocation++;
        // Aggregate time allocation data - simply sum up all values (including unallocated)
        Object.entries(dayData.timeAllocation).forEach(([category, percentage]) => {
          stats.companyAllocation[category] = (stats.companyAllocation[category] || 0) + (percentage as number);
        });
      }
      
      // Track participation stats by type
      if (type === 'PULSE') {
        stats.submitted++;
      } else if (['NO_INFO', 'TODAY'].includes(type)) {
        stats.missing++;
      }
    }
  }

  // Calculate average allocation percentages (simple averaging)
  if (totalEntriesWithAllocation > 0) {
    Object.keys(stats.companyAllocation).forEach(category => {
      stats.companyAllocation[category] = Math.round(stats.companyAllocation[category] / totalEntriesWithAllocation);
    });
  }

  // Calculate participation rates
  stats.total = stats.submitted + stats.missing + stats.ooo;
  stats.participationRate = (stats.submitted + stats.missing) > 0 
    ? Math.round((stats.submitted / (stats.submitted + stats.missing)) * 100) 
    : 0;

  return stats;
}

/**
 * Composable for calculating pulse statistics with comprehensive metrics
 * @param pulses - Reactive reference to array of CompanyPulseData
 * @returns Computed stats object with all participation and allocation data
 */
export function usePulseStats(pulses: Ref<CompanyPulseData[]>) {
  const stats = computed<PulseStats>(() => calculatePulseStats(pulses.value));
  return { stats };
}

/**
 * Legacy composable for allocation-focused statistics (maintained for backward compatibility)
 * @param pulses - Reactive reference to array of CompanyPulseData
 * @returns Computed allocation stats with limited interface for compatibility
 * @deprecated Use usePulseStats instead, which provides all the same data and more
 */
export function usePulseAllocationStats(pulses: Ref<CompanyPulseData[]>) {
  const fullStats = computed(() => calculatePulseStats(pulses.value));
  
  // Map to the legacy interface structure
  const stats = computed(() => ({
    totalEligibleEntries: fullStats.value.totalEligibleEntries,
    companyAllocation: fullStats.value.companyAllocation,
    participationRate: fullStats.value.participationRate
  }));
  
  return { stats };
}

/**
 * Utility function to calculate stats for a single user's pulse data
 * @param userPulses - Object with date keys and Pulse values
 * @returns Individual user stats
 */
export function calculateUserPulseStats(userPulses: Record<string, any>) {
  const userStats = {
    submitted: 0,
    missing: 0,
    ooo: 0,
    total: 0,
    participationRate: 0,
    hasTimeAllocation: false,
    avgAllocation: {} as Record<string, number>
  };

  let allocationDays = 0;
  const totalAllocation: Record<string, number> = {};

  for (const day of Object.keys(userPulses)) {
    const dayData = userPulses[day];
    const type = dayData.type;
    
    if (OMIT_TYPES.includes(type)) {
      if (type === 'OOO') userStats.ooo++;
      continue;
    }
    
    // Check for time allocation data (regardless of type)
    if (dayData.timeAllocation && Object.keys(dayData.timeAllocation).length > 0) {
      userStats.hasTimeAllocation = true;
      allocationDays++;
      Object.entries(dayData.timeAllocation).forEach(([category, percentage]) => {
        totalAllocation[category] = (totalAllocation[category] || 0) + (percentage as number);
      });
    }
    
    // Track participation stats by type
    if (type === 'PULSE') {
      userStats.submitted++;
    } else if (['NO_INFO', 'TODAY'].includes(type)) {
      userStats.missing++;
    }
  }

  // Calculate average allocation percentages
  if (allocationDays > 0) {
    Object.keys(totalAllocation).forEach(category => {
      userStats.avgAllocation[category] = Math.round(totalAllocation[category] / allocationDays);
    });
  }

  userStats.total = userStats.submitted + userStats.missing + userStats.ooo;
  userStats.participationRate = userStats.submitted + userStats.missing > 0 
    ? Math.round((userStats.submitted / (userStats.submitted + userStats.missing)) * 100)
    : 0;

  return userStats;
}

/**
 * Enhanced user allocation calculation that provides allocation percentages
 * @param userPulses - Object with date keys and Pulse values
 * @returns User allocation stats with percentage breakdown
 */
export function calculateUserAllocationStats(userPulses: Record<string, any>) {
  const allocation: Record<string, number> = {};
  let totalEligibleDays = 0;

  Object.values(userPulses || {}).forEach((pulse: any) => {
    if (OMIT_TYPES.includes(pulse.type)) return; // Exclude holidays, OOO, and future days
    
    totalEligibleDays++;
    
    // Check if this entry has time allocation data (backend now returns unallocated: 100 for missing data)
    if (pulse.timeAllocation && Object.keys(pulse.timeAllocation).length > 0) {
      // Simply sum up all allocation values - backend provides unallocated: 100 when needed
      Object.entries(pulse.timeAllocation).forEach(([category, percentage]) => {
        allocation[category] = (allocation[category] || 0) + (percentage as number);
      });
    }
  });

  // Calculate average allocation percentages (simple averaging)
  if (totalEligibleDays > 0) {
    Object.keys(allocation).forEach(category => {
      allocation[category] = Math.round(allocation[category] / totalEligibleDays);
    });
  }

  return { 
    allocation, 
    totalEligibleDays
  };
}

/**
 * Composable for calculating individual user pulse allocation statistics
 * @param userPulses - Reactive reference to user's PulseData
 * @returns Computed allocation stats for the user
 */
export function useUserAllocationStats(userPulses: Ref<Record<string, any>>) {
  const stats = computed(() => calculateUserAllocationStats(userPulses.value));
  return { stats };
}

/**
 * Metrics allocation data point (from backend /metrics/pulse-allocation)
 */
export interface MetricsAllocationDataPoint {
  day: string;
  user_id: number;
  allocation: Record<string, number>;
}

/**
 * Calculate aggregated allocation statistics from metrics data points
 * This is used by the metrics page to aggregate raw allocation data from the backend
 * 
 * @param dataPoints - Array of allocation data points (one per user per day)
 * @returns Aggregated allocation statistics
 */
export function calculateMetricsAllocationStats(dataPoints: MetricsAllocationDataPoint[]) {
  const categoryTotals: Record<string, number> = {};
  let totalEntries = dataPoints.length;

  // Aggregate all allocations
  for (const entry of dataPoints) {
    for (const [category, percentage] of Object.entries(entry.allocation)) {
      categoryTotals[category] = (categoryTotals[category] || 0) + percentage;
    }
  }

  // Calculate average percentages
  const companyAllocation: Record<string, number> = {};
  for (const [category, total] of Object.entries(categoryTotals)) {
    companyAllocation[category] = totalEntries > 0 ? Math.round(total / totalEntries) : 0;
  }

  return {
    companyAllocation,
    hasData: Object.keys(companyAllocation).length > 0
  };
}

/**
 * Composable for calculating metrics allocation statistics
 * @param dataPoints - Reactive reference to metrics allocation data points
 * @returns Computed aggregated allocation stats
 */
export function useMetricsAllocationStats(dataPoints: Ref<MetricsAllocationDataPoint[]>) {
  const stats = computed(() => calculateMetricsAllocationStats(dataPoints.value));
  return { stats };
}
